/**
 * 실패 응답을 화면이 쓸 수 있는 모양으로 바꾼다.
 *
 * ★ 상태 코드를 화면까지 **들려 보낸다.**
 *
 * 본문만 넘기면 화면은 «서버가 죽었다»(5xx)와 «요청이 틀렸다»(4xx)를 구분할 수
 * 없다. 실제로 그래서 개발에서 500(마이그레이션 누락)이 «링크가 만료되었습니다»
 * 로 보였다 — 사용자는 링크를 다시 받으러 갔고, 몇 번을 받아도 같은 화면을 봤다.
 *
 * 이름을 `status` 가 아니라 `httpStatus` 로 둔다. 응답 본문에 `status` 필드를 쓰는
 * API 가 있어서, 덮어쓰면 그쪽이 조용히 틀어진다.
 *
 * ## 왜 `axios.ts` 가 아니라 여기인가
 *
 * 확인할 수 있어야 해서다. 인터셉터 안에 인라인으로 두면 「5xx 를 가려낸다」 를
 * 눈으로 믿는 수밖에 없는데, 처음 쓴 판이 바로 그래서 틀렸다 — 평문 본문을
 * 객체로 보고 분기를 통째로 못 태웠다.
 *
 * 그리고 `axios.ts` 를 테스트에서 부르면 **axios 가 ESM 이라 파싱에서 죽는다.**
 * 이 파일은 아무것도 import 하지 않으므로 그냥 불린다.
 */
export const shapeError = (error: any) => {
  // ★ **응답을 아예 못 받았다**(`response` 없음) — 연결이 끊겼거나, 응답에 CORS 머리글이 없어
  //   브라우저가 막았다(ALB 가 직접 내는 502·503·504 가 그렇다). 상태 코드가 없으니 화면은
  //   5xx 와 4xx 어느 쪽으로도 못 간다 — 그래서 «인증에 실패했습니다» 같은 기본 문구로 떨어졌다
  //   (2026-09-23 공격 시험 f1-0 화면 쪽). 표시를 달아 `failureMessage` 가 가를 수 있게 한다.
  if (error && !error.response) {
    return { httpStatus: undefined, noResponse: true, body: 'Something went wrong' };
  }
  const raw = (error?.response && error.response.data) || 'Something went wrong';
  const httpStatus = error?.response?.status;
  if (raw && typeof raw === 'object') {
    if (!('httpStatus' in raw)) raw.httpStatus = httpStatus;
    return raw;
  }
  // 평문 본문은 `detail` 에 **넣지 않는다.**
  //
  // 500 은 FastAPI 가 아니라 Starlette 가 `text/plain` 으로 «Internal Server
  // Error» 를 돌려준다(이 앱에 예외 핸들러가 없다). 화면들은 `error?.detail` 을
  // 그대로 띄우므로, 여기에 담으면 사용자가 그 영어 문장을 보게 된다. 담지
  // 않으면 각 화면의 한국어 기본 문구가 그대로 뜬다.
  return { httpStatus, body: raw };
};

/**
 * 서버가 **사람에게 보여 주려고** 쓴 문장만 꺼낸다. 없으면 `null` — 화면이 자기
 * 기본 문구를 쓴다.
 *
 * `shapeError` 는 JSON 본문을 그대로 넘기므로 `detail` 의 모양이 제각각이다:
 *
 *     400 · 403 · 404   문자열 — 서버가 쓴 한국어 문장 («데이터 비교 날짜를 확인해주세요»)
 *     422               **배열**(FastAPI 검증 오류 목록) — 그대로 넣으면 화면에 객체가 들어간다
 *     500               문자열이지만 «Internal Server Error» — 사람에게 보일 문장이 아니다
 *                       (P2 부터 500 도 JSON 이라 평문 분기로 걸러지지 않는다)
 *     응답 없음          `detail` 이 없다
 *
 * 그래서 «4xx 의 비어 있지 않은 문자열» 만 통과시킨다.
 */
export const serverDetail = (error: any): string | null => {
  if (!error || error.noResponse) return null;
  if (typeof error.httpStatus === "number" && error.httpStatus >= 500) return null;
  const detail = error.detail;
  if (typeof detail !== "string" || detail.trim() === "") return null;
  return detail;
};
